import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth/context";
import { useCompletions } from "@/lib/hooks/useCompletions";
import { useChoreProposals } from "@/lib/hooks/useChoreProposals";
import { useInstantSubmissions } from "@/lib/hooks/useInstantSubmissions";
import { cn } from "@/lib/utils/cn";

type Item = { href: string; label: string; icon: string };

// Child v2 nav — emoji icons per design handoff §5.7.
// "Trofea" = gamifikacja (poziom, ranga, odznaki) → /stats.
// "Ja" maps to /settings (profile + preferences).
const childItems: Item[] = [
  { href: "/dashboard", label: "Start", icon: "🏠" },
  { href: "/chores", label: "Zadania", icon: "📋" },
  { href: "/wallet", label: "Skarbonka", icon: "🐷" },
  { href: "/stats", label: "Trofea", icon: "🏆" },
  { href: "/settings", label: "Ja", icon: "😎" },
];

const parentItems: Item[] = [
  { href: "/dashboard", label: "Start", icon: "🏠" },
  { href: "/chores", label: "Zadania", icon: "📋" },
  { href: "/approvals", label: "Sprawdź", icon: "✅" },
  { href: "/wallet", label: "Skarbonka", icon: "🐷" },
  { href: "/settings", label: "Ja", icon: "😎" },
];

export function BottomNav() {
  const { pathname } = useLocation();
  const { userDoc } = useAuth();
  const role = userDoc?.role;
  const isParent = role === "parent";
  const items = isParent ? parentItems : childItems;
  // Licznik czerwonej kropki na "Sprawdź" — cała kolejka rodzica:
  // wykonania zadań, szybkie zgłoszenia i propozycje.
  const { completions: pending } = useCompletions(
    isParent ? userDoc?.householdId : undefined,
    { status: "pending" },
  );
  const { proposals: pendingProposals } = useChoreProposals(
    isParent ? userDoc?.householdId : undefined,
    { status: "pending" },
  );
  const { submissions: pendingSubmissions } = useInstantSubmissions(
    isParent ? userDoc?.householdId : undefined,
    { status: "pending" },
  );
  const pendingCount = isParent
    ? pending.length + pendingProposals.length + pendingSubmissions.length
    : 0;

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-line"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <ul
        className="mx-auto max-w-md grid"
        style={{
          gridTemplateColumns: `repeat(${items.length}, minmax(0,1fr))`,
        }}
      >
        {items.map(({ href, label, icon }) => {
          const [hrefPath] = href.split("?");
          const active =
            pathname === hrefPath || pathname.startsWith(hrefPath + "/");
          const badge = href === "/approvals" ? pendingCount : 0;

          return (
            <li key={`${href}-${label}`}>
              <Link
                to={href}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 py-2 text-[11px]",
                  active ? "text-green font-bold" : "text-slate font-semibold",
                )}
                aria-label={
                  badge > 0 ? `${label}, ${badge} do zatwierdzenia` : label
                }
              >
                <span
                  className={cn(
                    "relative inline-flex items-center justify-center w-10 h-8 rounded-[14px] text-[20px] leading-none transition-all duration-150",
                    active
                      ? "bg-green text-white shadow-[0_2px_0_var(--kd-forest)]"
                      : "bg-transparent text-navy",
                  )}
                  aria-hidden
                >
                  {icon}
                  {badge > 0 && (
                    <span className="absolute -top-0.5 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-extrabold leading-none flex items-center justify-center font-num ring-2 ring-white">
                      {badge > 9 ? "9+" : badge}
                    </span>
                  )}
                </span>
                <span>{label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
